<template>
    <Dialog
        v-if="items.length > 0"
        v-model:open="isOpen"
        @update:open="onOpenChange">
        <DialogContent
            :class="cn('max-w-[37.5rem] lg:max-w-[50rem] outline-none')"
            @open-auto-focus.prevent>
            <div ref="cardRef" class="relative bg-background w-full rounded-2xl md:rounded-3xl overflow-hidden">
                <div class="flex items-center md:gap-4 p-4 md:p-5">
                    <div class="swiper flex-1 min-w-0">
                        <div class="swiper-wrapper">
                            <div
                                v-for="(item, index) in items"
                                :key="index"
                                class="swiper-slide">
                                <div class="msig-popup-announcement-content flex flex-col gap-4 md:gap-6">
                                    <div
                                        v-if="item.imageUrl"
                                        class="w-full aspect-video max-h-[18rem] rounded-xl overflow-hidden">
                                        <img
                                            :src="item.imageUrl"
                                            :alt="item.imageAlt || ''"
                                            class="w-full h-full object-cover" />
                                    </div>

                                    <div
                                        v-if="item.content"
                                        v-html="item.content"></div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                <div
                    v-if="items.length > 1"
                    class="flex items-center md:gap-4 px-4 md:px-5 mt-5 md:mt-6">
                    <div class="hidden md:block shrink-0 size-10"></div>
                    <div class="flex flex-1 min-w-0 justify-center">
                        <div class="msig-popup-bullets flex gap-2"></div>
                    </div>
                    <div class="hidden md:block shrink-0 size-10"></div>
                </div>

                <div class="flex items-center md:gap-4 px-4 md:px-5 mt-5 md:mt-6 pb-6 md:pb-8">

                    <div class="flex flex-1 min-w-0 cursor-pointer items-center justify-between">
                        <div v-if="items.length > 1" class="flex gap-2 items-center">
                            <button
                                type="button"
                                class="msig-popup-prev shrink-0 hidden md:flex items-center justify-center size-10 bg-white border rounded-2xl text-secondary disabled:opacity-40">
                                <ChevronLeft class="size-5" />
                                <span class="sr-only">Previous</span>
                            </button>
                            <button
                                type="button"
                                class="msig-popup-next shrink-0 hidden md:flex items-center justify-center size-10 bg-white border rounded-2xl text-secondary disabled:opacity-40">
                                <ChevronRight class="size-5" />
                                <span class="sr-only">Next</span>
                            </button>
                        </div>
                        <DialogClose as-child>
                            <slot :close>
                                <MsigButton
                                    button-style="primary"
                                    button-size="md"
                                >
                                    Close
                                </MsigButton>
                            </slot>
                        </DialogClose>
                    </div>
                </div>
            </div>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import {
    Dialog,
    DialogContent,
    DialogClose,
} from '@/components/ui/dialog'
import { MsigButton } from '@/components/website/button'
import { cn } from '@/lib/utils'
import { ChevronLeft, ChevronRight } from 'lucide-vue-next'
import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
} from 'vue'

interface NoticeItem {
    imageUrl?: string
    imageAlt?: string
    content?: string
    expiryDate?: string
}

interface Props {
    announcements?: string
    /** Hours to suppress the popup after the user dismisses it. */
    intervalHours?: number
}

const props = withDefaults(defineProps<Props>(), {
    intervalHours: 24,
})

const items = computed<NoticeItem[]>(() => JSON.parse(props.announcements || '[]') )


// localStorage key — suppression duration is driven by `intervalHours`
const STORAGE_KEY = 'msig-popup-announcement-dismissed-at'

const isOpen = ref(false)
const cardRef = ref<HTMLElement | null>(null)

const close = () => isOpen.value = false
    
let swiperInstance: { destroy: () => void } | null = null

const isRecentlyDismissed = (): boolean => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return false
        const at = parseInt(raw, 10)

        if (Number.isNaN(at)) return false
        const suppressionMs = props.intervalHours * 60 * 60 * 1000
        console.log('AnnouncementDialog:', props.intervalHours)
        console.log('AnnouncementDialog: now', Date.now())
        console.log('AnnouncementDialog: suppression duration', suppressionMs)
        return Date.now() - at < suppressionMs
    } catch {
        return false
    }
}

const recordDismissal = () => {
    try {
        localStorage.setItem(STORAGE_KEY, String(Date.now()))
    } catch {
        // localStorage might be disabled — silently ignore
    }
}

// Fires whenever the dialog's open state flips. We record the dismissal
// here directly (rather than via a `watch` on `isOpen`) so the write
// happens even if reactivity timing varies.
const onOpenChange = (open: boolean) => {
    if (!open) {
        recordDismissal()
        teardownSwiper()
    }
}

const initSwiper = async () => {
    if (!cardRef.value) return

    const [{ Swiper }, { Navigation, Pagination }] = await Promise.all([
        import('swiper'),
        import('swiper/modules'),
        // @ts-ignore
        import('swiper/css'),
    ])

    const card = cardRef.value
    const swiperEl = card.querySelector('.swiper') as HTMLElement | null
    if (!swiperEl) return

    swiperInstance = new Swiper(swiperEl, {
        modules: [Navigation, Pagination],
        slidesPerView: 1,
        spaceBetween: 32,
        navigation: {
            prevEl: card.querySelector('.msig-popup-prev') as HTMLElement,
            nextEl: card.querySelector('.msig-popup-next') as HTMLElement,
        },
        pagination: {
            el: card.querySelector('.msig-popup-bullets') as HTMLElement,
            clickable: true,
            bulletElement: 'button',
            bulletClass:
                'rounded-full transition-all duration-300 ease-in size-3 bg-slate-200 dark:bg-midGrey',
            bulletActiveClass: '!w-9 !bg-primary dark:!bg-slate-200',
        },
    })
}

const teardownSwiper = () => {
    swiperInstance?.destroy()
    swiperInstance = null
}

onMounted(async () => {
    if (items.value.length === 0) return
    if (isRecentlyDismissed()) return

    isOpen.value = true

    // Wait for the dialog DOM to render before initialising Swiper
    await nextTick()
    await initSwiper()
})

onBeforeUnmount(() => {
    teardownSwiper()
})
</script>

<style>
@import 'swiper/css';
</style>
